import { useEffect, useMemo, useRef, useState } from "react";
import { AlarmClock } from "lucide-react";
import { useNavCountsContext } from "../nav/NavCountsContext";
import type { StatutoryClock } from "../nav/NavCountsContext";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { useMobile } from "../context/MobileContext";

// The statutory clock in the top bar.
//
// Every deadline the platform derives — a notification window from lib/resilienceRules.ts, a
// submission's own dueAt — has until now only ever been a row in a table on a page somebody had
// to open. Inside a 24-hour window that is too late to be a control. This is the same deadline,
// running, in front of everyone, and clicking it lands on the thing that satisfies it.
//
// It ticks locally but is never the source of truth: the countdown is always measured against the
// server's `dueAt`, corrected by the skew between the server's `now` and this browser's, so a
// laptop with a wrong clock shows the right time remaining. GET /nav/counts re-reads it every
// minute (nav/useNavCounts.ts); only the display ticks in between.

const SECOND = 1000;

/**
 * The warning window, mirroring `submissionUrgency()` in backend/src/lib/regulatorySubmissions.ts.
 * The urgency travels on the payload too, but the payload is a minute old at worst — a deadline
 * that passes while somebody is looking at the screen has to turn red as it passes, not at the
 * next poll.
 */
const DUE_SOON_HOURS = 4;

function urgencyOf(remainingMs: number): StatutoryClock["urgency"] {
  if (remainingMs < 0) return "overdue";
  if (remainingMs <= DUE_SOON_HOURS * 3_600_000) return "due_soon";
  return "on_time";
}

const TONE: Record<StatutoryClock["urgency"], { fg: string; bg: string; border: string; word: string }> = {
  overdue:  { fg: "var(--c-dangerText)",  bg: "var(--c-dangerBg)",  border: "var(--c-danger)",  word: "Overdue" },
  due_soon: { fg: "var(--c-warningText)", bg: "var(--c-warningBg)", border: "var(--c-warning)", word: "Due" },
  on_time:  { fg: "var(--c-accentText)",  bg: "var(--c-accentBg)",  border: "var(--c-accent)",  word: "Due" },
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The countdown itself.
 *
 * Seconds appear inside a day and not before: a ticking second hand two days out is noise, but in
 * the last hours it is the whole point of putting a clock on the screen.
 */
export function formatRemaining(ms: number): string {
  const abs = Math.abs(ms);
  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor((abs % 86_400_000) / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  const seconds = Math.floor((abs % 60_000) / 1000);
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}`;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/** The same figure in words, for the screen reader and the tooltip. */
export function describeRemaining(ms: number): string {
  const abs = Math.abs(ms);
  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor((abs % 86_400_000) / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  const parts = [
    days > 0 ? `${days} day${days === 1 ? "" : "s"}` : null,
    hours > 0 ? `${hours} hour${hours === 1 ? "" : "s"}` : null,
    days === 0 ? `${minutes} minute${minutes === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  const said = parts.length > 0 ? parts.join(" ") : "less than a minute";
  return ms < 0 ? `overdue by ${said}` : `due in ${said}`;
}

export function DeadlineClock() {
  const { counts } = useNavCountsContext();
  const navigate = usePortalNavigate();
  const isMobile = useMobile();
  const deadline = counts?.role === "client" ? counts.deadline : null;

  // Skew: how far this browser's clock is from the server's, measured when the payload arrived.
  const skew = useRef(0);
  const anchoredTo = useRef<string | null>(null);
  if (deadline && anchoredTo.current !== deadline.now) {
    anchoredTo.current = deadline.now;
    skew.current = Date.parse(deadline.now) - Date.now();
  }

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!deadline) return;
    const id = window.setInterval(() => setTick((t) => t + 1), SECOND);
    return () => window.clearInterval(id);
  }, [deadline]);

  const due = useMemo(() => (deadline ? Date.parse(deadline.dueAt) : 0), [deadline]);
  if (!deadline) return null;

  const remaining = due - (Date.now() + skew.current);
  const urgency = urgencyOf(remaining);
  const tone = TONE[urgency];
  const others = deadline.outstanding - 1;
  const spoken = describeRemaining(remaining);
  const title = [
    `${deadline.label} — ${spoken}`,
    deadline.detail,
    `Deadline ${new Date(due).toLocaleString()}`,
    others > 0 ? `${others} other deadline${others === 1 ? "" : "s"} running` : null,
  ].filter(Boolean).join("\n");

  return (
    <>
      <style>{`@keyframes deadline-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
      <button
        type="button"
        onClick={() => navigate(deadline.href)}
        title={title}
        aria-label={`${deadline.label}, ${spoken}. Open it.`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: isMobile ? "4px 7px" : "5px 10px",
          borderRadius: 2,
          border: `1px solid ${tone.border}`,
          background: tone.bg,
          color: tone.fg,
          cursor: "pointer",
          font: "inherit",
          lineHeight: 1,
          maxWidth: isMobile ? 132 : 320,
          transition: "filter 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(0.96)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
      >
        <AlarmClock
          size={15}
          style={{
            flexShrink: 0,
            // Only the last four hours and a missed deadline animate. Everything else would be a
            // thing on the screen that moves for no reason, which people learn to stop seeing.
            animation: urgency === "on_time" ? undefined : "deadline-pulse 2s ease-in-out infinite",
          }}
        />
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              fontFeatureSettings: '"tnum"',
              letterSpacing: "0.02em",
            }}
          >
            {remaining < 0 ? "-" : ""}{formatRemaining(remaining)}
          </span>
          {!isMobile && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                opacity: 0.85,
                maxWidth: 240,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tone.word}: {deadline.label}{others > 0 ? ` +${others}` : ""}
            </span>
          )}
        </span>
      </button>
    </>
  );
}
